import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  usePreviewPageContext,
  useSiteConfig,
  ComponentLoadingStatus,
} from '@bingads-webui-campaign-react/smart-page-context';

import { DefaultButton } from '@fluentui/react/lib/Button';

const getOffsetFromCropSettings = (cropSettings) => {
  const { cropY, cropHeight } = cropSettings;

  if (!cropHeight) {
    return 0;
  }

  return -(100 * cropY) / cropHeight;
};

export const ImageCropperPreview = (props) => {
  const {
    i18n,
    classes,
    src,
    cropSettings,
    activate,
    isInEditor,
    isExternalSrc,
  } = props;

  const offset = useMemo(() => getOffsetFromCropSettings(cropSettings), [cropSettings]);

  const { updateElementsLoadingStatus } = usePreviewPageContext();
  const { dispatchSiteConfig } = useSiteConfig();

  const handleOnLoad = useCallback((e) => {
    if (!isInEditor) {
      updateElementsLoadingStatus({ coverImage: { status: ComponentLoadingStatus.LOADED } });
    }

    if (isExternalSrc) {
      const { height, width } = e.currentTarget;

      dispatchSiteConfig({
        type: 'UPDATE_COVER_IMAGE',
        payload: {
          imageWidth: width,
          imageHeight: height,
        },
      });
    }
  }, [
    updateElementsLoadingStatus,
    isInEditor,
    dispatchSiteConfig,
    isExternalSrc,
  ]);

  return (
    <div>
      <div className={classNames('image-cropper-preview', classes.preview)}>
        <picture
          style={{
            position: 'absolute',
            top: `${offset}%`,
            left: 0,
            right: 0,
          }}
        >
          <img
            onLoad={handleOnLoad}
            src={src}
            className={classes.img}
            alt={i18n.getString(_TL_('Cover image'))}
          />
        </picture>
      </div>
      {
        isInEditor &&
        <div
          className={classNames('image-cropper-preview-overlay', classes.overlayPreview)}
          role="button"
          tabIndex="0"
          onClick={activate}
          onKeyPress={activate}
        >
          <DefaultButton
            className={classNames(classes.overlayMessage, classes.unclickable)}
            iconProps={{ iconName: 'Move' }}
          >
            {i18n.getString(_TL_('Click to edit'))}
          </DefaultButton>
        </div>
      }
    </div>
  );
};

ImageCropperPreview.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    img: PropTypes.string.isRequired,
  }).isRequired,
  src: PropTypes.string.isRequired,
  cropSettings: PropTypes.shape({}).isRequired,
  activate: PropTypes.func.isRequired,
  isInEditor: PropTypes.bool.isRequired,
  isExternalSrc: PropTypes.bool.isRequired,
};
